import { UserProfile } from "@api/models/UserProfile";
import { Element } from "@api/models/Element";
import { UserRepository } from "@api/repositories/UserRepository";
import { EventMembershipRepository } from "@api/repositories/EventMembershipRepository";

import { sleep } from "@share/web-conferences/helpers/SleepHelper";
import { DuplicateAction, ImportRowData, Progress, ProgressState, ResultOfApplyingData, UserProfileDuplicate } from "../types";
import { getMapsByFields, mapToProfileDuplicate } from "./mapHelper";
import { EventMembershipTypes } from "@api/enums/EventMembershipTypes";
import { PropertiesInterface } from "@api/models/settings/PropertiesInterface";
import { UserCreateParams } from "@api/models/interfaces/user/UserCreateParams";
import { getCodeGenerator } from "./codes";
import { ObjectRepository } from "@api/repositories/ObjectRepository";
import { Store } from "vuex";
import { UserProfileInterface } from "@api/models/interfaces/UserProfileInterface";
import { AxiosResponse } from "axios";
import { ArrayHelpers } from "@common/ArrayHelpers";
import { UserCreateEditServiceInterface } from "@share/common/services/userCreateEdit/interfaces/UserCreateEditServiceInterface";

export class ParticipantsImportService {

    FIELDS_FOR_COMPARE = [['email'], ['phoneNumber'], ['firstName', 'lastName'], ['code']];

    codeGenerationSettings?: PropertiesInterface;
    userCodes?: Set<string>;

    readonly CHUNK_SIZE_FOR_PROFILES_CREATE = 600;

    constructor(
        private userRepository: UserRepository,
        private eventMembershipRepository: EventMembershipRepository,
        private objectRepository: ObjectRepository,
        private eventId: string,
        private store: Store<any>,
        private userCreateEditService: UserCreateEditServiceInterface
    ) {

        this.userCreateEditService.getCodeGenerationSettings()
            .then(({ codeAlphabet, codePrefix, codeRandomLen }) => {
                this.codeGenerationSettings = {
                    codeAlphabet,
                    codePrefix,
                    codeRandomLen
                }
            })
    }

    /**
     * Получение существуюих данных
     * @param fields
     * @returns
     */
    private async getSystemData(fields: string[]) {

        const query = {
            include: ['id', 'userId', 'code', ...fields],
            skip: 0,
            take: -1
        }

        return this.userRepository.profiles(query);
    }

    private async getParticipants() {
        return await this.eventMembershipRepository.query(
            process.env.VUE_APP_EVENTS_SCHEMA!,
            [this.eventId],
            [EventMembershipTypes.speaker, EventMembershipTypes.participant]
        )
    }

    async getDuplicates(importData: ImportRowData[], fields: string[], progress: Progress) {

        progress.state = ProgressState.prepare;

        await sleep(100);

        //TODO: добавить запросы ниже в Promise.All
        //TODO: заменить на будущий метод getAllProfiles в UserCreateEditService

        //получение всех профилей
        const systemData = await this.getSystemData(fields);

        // получить данные об участниках
        let participants: Element[] | null = await this.getParticipants();

        //мапы для получения элемента по необходимому ключу
        const [emailKeys, phoneNumberKeys, nameKeys, codeKeys] = getMapsByFields(systemData, this.FIELDS_FOR_COMPARE);

        this.userCodes = new Set(codeKeys.keys());

        const duplicatesDataSet: Set<number> = new Set();
        const profilesDataSet: Set<UserProfile> = new Set();
        const membershipMap: Map<number, EventMembershipTypes | null> = new Map();
        const duplicatesList: UserProfileDuplicate[] = [];

        //обработка записей
        progress.state = ProgressState.counting;

        /**
         *  comparing import data fields with data from db
         */
        for (let index = 0; index < importData.length; index++) {

            profilesDataSet.clear();

            let importedItem = importData[index];

            //check email
            if (emailKeys.has(importedItem.email as string)) {
                this.setProfileData(profilesDataSet, emailKeys.get(importedItem.email as string));
            }

            //check phoneNumber
            if (phoneNumberKeys.has(importedItem.phoneNumber?.toString())) {
                this.setProfileData(profilesDataSet, phoneNumberKeys.get(importedItem.phoneNumber?.toString()));
            }

            //check fullName string
            const fullNameString = importedItem.firstName as string + importedItem.lastName as string;
            if (nameKeys.has(fullNameString)) {
                this.setProfileData(profilesDataSet, nameKeys.get(fullNameString));
            }

            if (importData.length < 15) {
                await sleep(10);
            }

            progress.count++;

            if (!profilesDataSet.size) {
                continue
            }

            //получаем необходимую структуру списком
            const profileDuplicate = this.getProfileDuplicate(Array.from(profilesDataSet.values()), importedItem);

            profileDuplicate.forEach((item) => {
                duplicatesList.push(item);
                duplicatesDataSet.add(item.profile?.userId || 0);
            });
        }

        progress.state = ProgressState.completing;
        await sleep(300);

        //создать map с ролями
        participants.forEach((participant) => {
            if (duplicatesDataSet.has(participant.userId)) {
                membershipMap.set(participant.userId, participant.type)
            }
        });

        participants = null;

        // выставить флаг участия для каждого дубля
        duplicatesList.forEach((duplicate) => {
            duplicate.role = membershipMap.get(duplicate.profile?.userId || 0)
        });

        return duplicatesList;
    }

    async applyImport(duplicates: UserProfileDuplicate[] = [], rowsForCreate: ImportRowData[] = [], progress: Progress): Promise<ResultOfApplyingData> {

        //ADDUSERS: создание пользователей и их профилей + добавление их в текущее мероприятие

        let createdProfiles: UserProfileInterface[] = await this.createUsersByChunks(rowsForCreate, progress);
        let updatedProfiles: UserProfileInterface[] = [];

        if (duplicates.length) {

            // duplicates wich not in event - users for add to current event
            {
                const duplicatesNotInEventUserIds = this.getDuplicatesNotInEventUserIds(duplicates);
                if (duplicatesNotInEventUserIds.length) {
                    this.addUsersToEvent(duplicatesNotInEventUserIds);
                }
            }

            await sleep(50);

            //UPDATE:
            {
                //duplicates for update
                const forUpdate = duplicates.filter((item) => item.action?.id === DuplicateAction.update);

                if (forUpdate.length) {
                    updatedProfiles = (await this.updateUsers(forUpdate))?.map(result => result.data);
                }
            }
        }

        return {
            created: createdProfiles,
            updated: updatedProfiles
        }
    }

    async createUsersByChunks(items: ImportRowData[], progress: Progress) {
        const chunkSize = this.CHUNK_SIZE_FOR_PROFILES_CREATE;
        const chunks = ArrayHelpers.splitToChunks(items, chunkSize);
        let result: UserProfileInterface[] = [];

        progress.total = chunks.length;

        let index = 0;

        for (index; index < chunks.length; index++) {

            const proilesForCreate = await this.createUsersAndGetProfilesForCreate(chunks[index]) as UserProfileInterface[];
            const profilesCreateResponse = await this.createProfilesAndAddUsersToEvent(proilesForCreate);

            if (profilesCreateResponse.length) {
                result = result.concat(profilesCreateResponse);

                progress.count++;
            }
        }

        return result;
    }

    async createUsersAndGetProfilesForCreate(items: ImportRowData[]) {
        //создавать нечего
        if (!items.length) {
            return [];
        }

        //1. создание генератора кода
        //TODO: this code executed in loop, getCodeGenerator must be moved outside loop
        const getCode = getCodeGenerator(this.userCodes, this.codeGenerationSettings);

        if (!getCode) {
            throw new Error("code generator is not exist");
        }

        const importDataMap = new Map<string, ImportRowData>();

        const itemsForCreate: UserCreateParams[] = items.map(item => {
            const code = getCode().toLowerCase();

            importDataMap.set(code, item)

            return {
                password: code,
                username: code,
                roleId: "Authenticated"
            }
        });

        //2. массовое создание пользователей
        const createdUsers = await this.userCreateEditService.createUsersBatch(itemsForCreate) || [];

        //3. Подготовка данных для создания профилей
        return createdUsers.map((item) => {
            const importItem = item.username && importDataMap.get(item.username);

            if (importItem) {
                return {
                    ...importItem,
                    userId: item.id,
                    code: item.username
                }
            }
            else {
                throw new Error(`imported item not found by generated code`)
            }
        });
    }

    private async createProfilesAndAddUsersToEvent(proilesForCreate: UserProfileInterface[]): Promise<UserProfileInterface[]> {
        const createdProfiles = await this.userCreateEditService.createProfilesBatch(proilesForCreate) as UserProfileInterface[];

        if (createdProfiles.length) {
            const createdUserIds = this.getUserIds(createdProfiles);
            //TODO: заменить на будущий метод addUsersToEvent в UserCreateEditService
            this.addUsersToEvent(createdUserIds);

            return createdProfiles;
        }

        return [];
    }

    private async updateUsers(rowsData: UserProfileDuplicate[]): Promise<AxiosResponse<UserProfileInterface>[]> {

        //обновление профилей по одному
        const promises = rowsData.map(({ profile, source }) => {
            if (profile && profile.userId && source) {
                return this.objectRepository.update<UserProfileInterface>("UserProfiles", profile.id, source)
            }
        }).filter(item => !!item) as Promise<AxiosResponse<UserProfileInterface>>[];

        return Promise.all(promises)
    }

    /**
     * добавить пользователей в мероприятие
     * @param userIds
     */
    private async addUsersToEvent(userIds: number[]) {
        try {
            this.userCreateEditService.addUsersToEvent(userIds, this.eventId);
        }
        catch(e) {
            this.store.dispatch("reportError", e);
        }
    }

    private setProfileData(set: Set<UserProfile>, profile: UserProfile | UserProfile[] | undefined) {
        if (Array.isArray(profile)) {
            profile.forEach((item) => {
                set.add(item)
            })
        }
        else if(profile) {
            set.add(profile)
        }
    }

    private getProfileDuplicate(profileData: UserProfile[], importedItem: ImportRowData): UserProfileDuplicate[] {
        return profileData.map((item) => mapToProfileDuplicate(item, importedItem));
    }

    private getUserIds(items: UserProfileInterface[]) {
        return items.map((profile) => profile.userId).filter(userid => userid) as number[];
    }

    private getDuplicatesNotInEventUserIds(duplicates: UserProfileDuplicate[]) {
        return duplicates
            .filter(item => !item.role && item.profile?.userId && item.action?.id !== DuplicateAction.add)
            .map((item) => item.profile?.userId as number);
    }
}